import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, KeyRound, ArrowRight, Sparkles, LogOut, Check, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ETSY_SHOP_URL, goToEtsy } from "@/lib/site";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Paywall() {
  const { setHasAccess, setHasWedding, setUser, logout } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const redeem = async () => {
    setError("");
    if (!code.trim()) { setError("Please enter your access code."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/redeem", { code });
      setUser(data.user);
      setHasAccess(true);
      toast.success("Access unlocked — welcome!");
      navigate("/onboarding");
    } catch (e) {
      setError(apiErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const exploreDemo = async () => {
    setLoading(true);
    try {
      await api.post("/demo");
      setHasWedding(true);
      toast.success("Demo planner loaded");
      navigate("/app");
    } catch { toast.error("Couldn't load the demo. Please try again."); setLoading(false); }
  };

  const doLogout = async () => { navigate("/", { replace: true }); await logout(); };

  return (
    <div className="min-h-screen bg-background grain relative flex flex-col">
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><Heart className="h-4 w-4 text-primary-foreground" fill="currentColor" /></div>
          <span className="text-xl font-serif font-medium tracking-tight text-primary">PlanMyWedding</span>
        </div>
        <button onClick={doLogout} className="text-sm text-muted-foreground inline-flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="paywall-logout"><LogOut className="h-4 w-4" /> Log out</button>
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-accent font-semibold mb-5">
            <span className="h-px w-8 bg-accent" /> Unlock your planner
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-primary leading-tight">Unlock your planner.</h1>
          <p className="text-muted-foreground mt-3">Bought PlanMyWedding on Etsy? Check your email for your secure access link — one click unlocks your planner. If you have an access code instead, enter it below.</p>

          {ETSY_SHOP_URL && (
            <div className="mt-6 rounded-2xl border border-accent/40 bg-accent/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="font-medium text-primary">Haven't purchased yet?</div>
                <div className="text-sm text-muted-foreground">Get lifetime access on our Etsy shop.</div>
              </div>
              <Button onClick={goToEtsy} className="rounded-full shrink-0" data-testid="paywall-etsy"><ShoppingBag className="h-4 w-4 mr-2" /> Get Access on Etsy</Button>
            </div>
          )}

          <div className="mt-8 rounded-2xl bg-card border border-border p-6">
            <label className="text-xs text-muted-foreground">Access code</label>
            <div className="relative mt-1">
              <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && redeem()}
                placeholder="PMW-XXXX-XXXX" className="pl-9 rounded-xl h-11 tracking-widest font-medium" data-testid="redeem-code-input" autoFocus />
            </div>
            {error && <p className="text-sm text-destructive mt-2" data-testid="redeem-error">{error}</p>}
            <Button onClick={redeem} disabled={loading} className="w-full rounded-full h-11 mt-4" data-testid="redeem-btn">
              {loading ? "Unlocking…" : "Unlock lifetime access"} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
              {["One-time payment, yours forever", "Every planning tool unlocked", "Access on all your devices"].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5 mr-3"><Check className="h-3.5 w-3.5 text-accent" /> {t}</li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground uppercase tracking-wider">not sure yet?</span><div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" onClick={exploreDemo} disabled={loading} className="w-full rounded-full h-11" data-testid="paywall-demo">
            <Sparkles className="h-4 w-4 mr-1.5" /> Explore the free demo first
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">Can't find your access link? Check your spam folder, or contact support with your Etsy order number.</p>
        </motion.div>
      </div>
    </div>
  );
}
